import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Typography,
    Chip,
    CircularProgress,
} from "@mui/material";
import { Building2, X, AlertTriangle } from "lucide-react";

import { AssignEntityModal } from "../../components/AssignEntityModal";
import { useTheme } from "../../theme/useTheme";
import apiClient from "../../api-client";

/**
 * Modal para asignar/remover consultores de agencias
 * Permite a SuperAdmin gestionar asignaciones multi-agencia
 */
export const AssignAgencyModal = ({
    open,
    onClose,
    consultant,
    allAgencies = [],
    onSuccess,
}) => {
    const { theme } = useTheme();
    const [selectedAgencyId, setSelectedAgencyId] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [removingAgencyId, setRemovingAgencyId] = useState(null);

    // Get consultant's current agencies
    const currentAgencies = consultant?.agencies || [];
    const currentAgencyIds = currentAgencies.map((a) => a.id);

    // Filter available agencies (not already assigned)
    const availableAgencies = allAgencies.filter(
        (agency) => !currentAgencyIds.includes(agency.id)
    );

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setSelectedAgencyId("");
            setError(null);
            setRemovingAgencyId(null);
        }
    }, [open]);

    const handleAssignAgency = async () => {
        if (!selectedAgencyId || !consultant?.id) return;

        setIsLoading(true);
        setError(null);

        try {
            const response = await apiClient.post(
                `users/${consultant.id}/invite_to_agency/`,
                { agency_id: selectedAgencyId }
            );

            if (response.data) {
                onSuccess?.("assign", response.data);
                setSelectedAgencyId("");
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al asignar agencia";
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveAgency = async (agencyId) => {
        if (!agencyId || !consultant?.id) return;

        // Note: The UI already prevents attempting to remove the primary agency
        // and the backend will also reject such requests with an appropriate error

        setRemovingAgencyId(agencyId);
        setError(null);

        try {
            const response = await apiClient.post(
                `users/${consultant.id}/remove_from_invited_agency/`,
                { agency_id: agencyId }
            );

            if (response.data) {
                onSuccess?.("remove", response.data);
            }
        } catch (err) {
            const errorMessage =
                err.response?.data?.error ||
                err.response?.data?.message ||
                "Error al eliminar agencia";
            setError(errorMessage);
        } finally {
            setRemovingAgencyId(null);
        }
    };

    if (!consultant) return null;

    const currentItems = currentAgencies.map((agency, index) => ({
        ...agency,
        isPrimary: index === 0,
    }));

    return (
        <AssignEntityModal
            open={open}
            onClose={onClose}
            title={`Gestionar agencias de ${consultant.name}`}
            currentTitle="Agencias actuales"
            currentItems={currentItems}
            renderCurrentItem={(agency) => (
                <Chip
                    key={agency.id}
                    icon={<Building2 size={14} />}
                    label={agency.isPrimary ? `${agency.name} (Principal)` : agency.name}
                    onDelete={
                        !agency.isPrimary ? () => handleRemoveAgency(agency.id) : undefined
                    }
                    deleteIcon={
                        removingAgencyId === agency.id ? (
                            <CircularProgress size={14} />
                        ) : (
                            <X size={14} />
                        )
                    }
                    disabled={removingAgencyId === agency.id}
                    sx={{
                        backgroundColor: agency.isPrimary
                            ? `${theme.palette.success.main}15`
                            : `${theme.palette.primary.main}15`,
                        color: agency.isPrimary
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                        border: `1px solid ${agency.isPrimary
                            ? theme.palette.success.main
                            : theme.palette.primary.main}30`,
                        "& .MuiChip-icon": {
                            color: agency.isPrimary
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                        },
                        "& .MuiChip-deleteIcon": {
                            color: theme.palette.text.secondary,
                            "&:hover": {
                                color: theme.palette.error.main,
                            },
                        },
                    }}
                />
            )}
            emptyCurrentText="Sin agencias asignadas"
            currentFooter={
                currentAgencies.length === 1 ? (
                    <Typography
                        variant="caption"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 1,
                            color: theme.palette.text.secondary,
                        }}
                    >
                        <AlertTriangle size={12} />
                        La agencia principal no se puede eliminar desde aquí
                    </Typography>
                ) : null
            }
            assignTitle="Asignar a nueva agencia"
            availableOptions={availableAgencies.map((agency) => ({
                value: agency.id,
                label: agency.name,
            }))}
            selectedValue={selectedAgencyId}
            onSelectValue={setSelectedAgencyId}
            assignPlaceholder="Buscar agencia..."
            onAssign={handleAssignAgency}
            assignDisabled={!selectedAgencyId}
            assignLoading={isLoading}
            emptyAvailableText="Este consultor ya está asignado a todas las agencias disponibles"
            error={error}
            onClearError={() => setError(null)}
        />
    );
};

AssignAgencyModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    consultant: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        email: PropTypes.string,
        agencies: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string,
            })
        ),
    }),
    allAgencies: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
        })
    ),
    onSuccess: PropTypes.func,
};

export default AssignAgencyModal;
